import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Api as AdministrationApi } from 'src/app/api/administration/api';
import { getCurrentUserFboLocations } from 'src/app/api/administration/fn/users/get-current-user-fbo-locations';
import {
  switchFboLocation,
  SwitchFboLocation$Params,
} from 'src/app/api/administration/fn/users/switch-fbo-location';
import { getAllFboLocations } from 'src/app/api/administration/fn/fbo-locations/get-all-fbo-locations';
import { createFboLocation } from 'src/app/api/administration/fn/fbo-locations/create-fbo-location';
import { getFboLocationDetails } from 'src/app/api/administration/fn/fbo-locations/get-fbo-location-details';
import { updateFboLocation } from 'src/app/api/administration/fn/fbo-locations/update-fbo-location';
import { deleteFboLocation } from 'src/app/api/administration/fn/fbo-locations/delete-fbo-location';
import { getAllFboLocationGroups } from 'src/app/api/administration/fn/fbo-locations/get-all-fbo-location-groups';
import { createFboLocationGroup } from 'src/app/api/administration/fn/fbo-locations/create-fbo-location-group';
import { getFboLocationGroupDetails } from 'src/app/api/administration/fn/fbo-locations/get-fbo-location-group-details';
import { updateFboLocationGroup } from 'src/app/api/administration/fn/fbo-locations/update-fbo-location-group';
import { deleteFboLocationGroups } from 'src/app/api/administration/fn/fbo-locations/delete-fbo-location-groups';

import { CurrentUserFboLocationsResponse } from 'src/app/api/administration/models/current-user-fbo-locations-response';
import { FboLocationMinimalResponse } from 'src/app/api/administration/models/fbo-location-minimal-response';
import { GetFboLocationsResponse } from 'src/app/api/administration/models/get-fbo-locations-response';
import { FboLocationResponse } from 'src/app/api/administration/models/fbo-location-response';
import { CreateFboLocationRequestDto } from 'src/app/api/administration/models';
import { UpdateFboLocationRequestDto } from 'src/app/api/administration/models';
import { GetFboLocationGroupsResponse } from 'src/app/api/administration/models/get-fbo-location-groups-response';
import { FboLocationGroupResponse } from 'src/app/api/administration/models/fbo-location-group-response';
import { FboLocationGroupRequestDto } from 'src/app/api/administration/models/fbo-location-group-request-dto';
import { ApiSuccess } from 'src/app/api/administration/models/api-success';

import { Api as FboApi } from 'src/app/api/fbo/api';
import {
  getAllLocations,
  GetAllLocations$Params,
} from 'src/app/api/fbo/fn/locations/get-all-locations';
import { GetLocationsResponse } from 'src/app/api/fbo/models/get-locations-response';

@Injectable({ providedIn: 'root' })
export class FboLocationApiService {
  private readonly api = inject(AdministrationApi);
  private readonly fboApi = inject(FboApi);

  /**
   * Get all locations from FBO API
   */
  getAllLocations(params?: GetAllLocations$Params): Observable<GetLocationsResponse> {
    return this.fboApi.invoke(getAllLocations, params);
  }

  /**
   * Get current user's FBO locations
   */
  getCurrentUserFboLocations(): Observable<CurrentUserFboLocationsResponse> {
    return this.api.invoke(getCurrentUserFboLocations);
  }

  /**
   * Switch active FBO location
   * @param locationId The location ID to switch to
   */
  switchFboLocation(locationId: number): Observable<FboLocationMinimalResponse> {
    return this.api.invoke(switchFboLocation, { body: { location: locationId } });
  }

  /**
   * Get all FBO locations with pagination
   * @param params Pagination and filter parameters
   */
  getAllFboLocations(params?: {
    page?: number;
    size?: number;
    search?: string;
    sort?: string;
  }): Observable<GetFboLocationsResponse> {
    return this.api.invoke(getAllFboLocations, params);
  }

  /**
   * Create a new FBO location
   * @param body FBO location data to create
   */
  createFboLocation(body: CreateFboLocationRequestDto): Observable<FboLocationResponse> {
    return this.api.invoke(createFboLocation, { body });
  }

  /**
   * Get FBO location details by ID
   * @param locationId The location ID to fetch
   */
  getFboLocationDetails(locationId: number): Observable<FboLocationResponse> {
    return this.api.invoke(getFboLocationDetails, { id: locationId });
  }

  /**
   * Update an existing FBO location
   * @param locationId The location ID to update
   * @param body FBO location data to update
   */
  updateFboLocation(
    locationId: number,
    body: UpdateFboLocationRequestDto
  ): Observable<FboLocationResponse> {
    return this.api.invoke(updateFboLocation, { id: locationId, body });
  }

  /**
   * Delete an FBO location
   * @param locationId The location ID to delete
   */
  deleteFboLocation(locationId: number): Observable<ApiSuccess> {
    return this.api.invoke(deleteFboLocation, { id: locationId });
  }

  // --- FBO Location Groups API ---

  getAllFboLocationGroups(params?: {
    page?: number;
    size?: number;
    search?: string;
    sort?: string;
  }): Observable<GetFboLocationGroupsResponse> {
    return this.api.invoke(getAllFboLocationGroups, params);
  }

  createFboLocationGroup(body: FboLocationGroupRequestDto): Observable<FboLocationGroupResponse> {
    return this.api.invoke(createFboLocationGroup, { body });
  }

  getFboLocationGroupDetails(id: number): Observable<FboLocationGroupResponse> {
    return this.api.invoke(getFboLocationGroupDetails, { id });
  }

  updateFboLocationGroup(
    id: number,
    body: FboLocationGroupRequestDto
  ): Observable<FboLocationGroupResponse> {
    return this.api.invoke(updateFboLocationGroup, { id, body });
  }

  deleteFboLocationGroupsBulk(ids: number[]): Observable<ApiSuccess> {
    return this.api.invoke(deleteFboLocationGroups, { ids });
  }

  deleteFboLocationGroups(id: number): Observable<ApiSuccess> {
    return this.api.invoke(deleteFboLocationGroups, { ids: [id] });
  }
}
